


import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ApiService } from "app/core/api/api.service";
import { bandActions } from "../actions/band.actions";
import { catchError, filter, map, of, switchMap, tap, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import * as fromApp from 'app/reducers/router.reducer'
import { SnackService } from "app/app-wrapper/snack.service";
import { Router } from "@angular/router";
import { WebSocketService } from "app/core/api/web-socket.service";


@Injectable()
export class BandEffects {

    loadBands$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.load),
        switchMap(() => this.api.getUserBands().pipe(
            map(bands => bandActions.loadSuccess({ response: bands })),
            catchError((e) => of(bandActions.loadError(e)))
        ))
    ));


    addBand$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.add),
        switchMap(({ band }) => this.api.createBand(band).pipe(
            tap(() => this.store.dispatch(bandActions.load())),
            map(band => bandActions.addSuccess({ response: band })),
            catchError((e) => of(bandActions.addError(e)))
        ))
    ));


    getMembers$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.loadMembers),
        withLatestFrom(this.store.select(fromApp.getRouterBandId)),
        filter(([_, bandId]) => !!bandId),
        switchMap(([_, bandId]) => {
            return this.api.getBandMembers(bandId).pipe(
                map(members => bandActions.loadMembersSuccess({ members })),
            )
        })
    ));

    getMembersOnRouteChange$ = createEffect(() => this.store.select(fromApp.getRouterBandId).pipe(
        filter(bandId => !!bandId),
        map(() => bandActions.loadMembers())
    ));

    inviteMember$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.inviteMember),
        switchMap(({ invite }) => this.api.inviteMember(invite).pipe(
            tap(() => this.snack.showSnack('APP.INVITE_SENT')),
            tap(() => this.store.dispatch(bandActions.loadPengingInvites())),
            map((res) => bandActions.inviteMemberSuccess({ response: res })),
            catchError((e) => {
                this.snack.showSnack('APP.USER_ALREADY_EXISTS', 'Error', 'contrast');
                return of(bandActions.inviteMemberError(e))
            })
        ))
    ));

    leaveBand$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.leaveBand),
        switchMap(({ bandId }) => this.api.leaveBand(bandId).pipe(
            tap(() => this.snack.showSnack('APP.LEFT_BAND')),
            tap(() => this.router.navigate(['/app'])),
            tap(() => this.store.dispatch(bandActions.load())),
            map((response) => bandActions.leaveBandSuccess({ response }))
        ))
    ));

    fireMember$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.fireMember),
        switchMap(({ bandId, memberId }) => this.api.fireMember(bandId, memberId).pipe(
            tap(() => this.snack.showSnack('APP.MEMBER_FIRED')),
            tap(() => this.store.dispatch(bandActions.loadMembers())),
            map((response) => bandActions.fireMemberSuccess({ response }))
        ))
    ));

    updateMember$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.updateMember),
        switchMap(({ dto }) => this.api.updateMember(dto).pipe(
            tap(() => this.snack.showSnack('APP.MEMBER_UPDATED')),
            tap(() => this.store.dispatch(bandActions.loadMembers())),
            map((response) => bandActions.updateMemberSuccess({ response }))
        ))
    ));

    loadPendingInvites$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.loadPengingInvites),
        withLatestFrom(this.store.select(fromApp.getRouterBandId)),
        filter(([_, bandId]) => !!bandId),
        switchMap(([_, bandId]) => this.api.getInvitesByBand(bandId).pipe(
            map((response) => bandActions.loadPendingInvitesSuccess({ response }))
        ))
    ));

    cancelPendingInvite$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.cancelInvite),
        withLatestFrom(this.store.select(fromApp.getRouterBandId)),
        switchMap(([{ inviteId }, bandId]) => this.api.deleteInvite(bandId, inviteId).pipe(
            tap(() => this.snack.showSnack('APP.INVITE_DECLINED')),
            tap(() => this.store.dispatch(bandActions.loadPengingInvites())),
            map((response) => bandActions.cancelInviteSuccess({ response }))
        ))
    ));

    deleteBand$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.deleteBand),
        switchMap(({ bandId }) => this.api.deleteBand(bandId).pipe(
            tap(() => this.snack.success('APP.BAND_DELETED')),
            tap(() => this.router.navigate(['/app'])),
            map((response) => bandActions.deleteBandSuccess({ response }))
        ))
    ));

    updateBand$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.updateBand),
        switchMap(({ band }) => this.api.updateBand(band.id, band).pipe(
            tap(() => this.store.dispatch(bandActions.load())),
            map((response) => bandActions.updateBandSuccess({ response }))
        ))
    ));

    updateBandSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(bandActions.updateBandSuccess),
        tap(() => this.snack.success('APP.BAND_UPDATED')),
        map(() => bandActions.load())
    ));

    onInviteMessage$ = createEffect(() => this.ws.onInviteNotification().pipe(
        tap(() => this.store.dispatch(bandActions.loadMembers())),
        map(() => bandActions.loadPengingInvites())
    ));








    constructor(
        private actions$: Actions,
        private api: ApiService,
        private store: Store,
        private snack: SnackService,
        private router: Router,
        private ws: WebSocketService
    ) { }

}
